import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

const buildEmpty = () => ({
  plate_number: "",
  make: "",
  model: "",
  color: "",
  status: "available",
  daily_rate: "",
  image_url: "",
  current_km: 0,
  last_oil_change_km: 0,
  last_oil_change_date: todayIso(),
  oil_change_interval_km: 5000,
  gps_device_id: "",
  notes: "",
});

export const CarFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(buildEmpty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        plate_number: editing.plate_number || "",
        make: editing.make || "",
        model: editing.model || "",
        color: editing.color || "",
        status: editing.status || "available",
        daily_rate: editing.daily_rate ?? "",
        image_url: editing.image_url || "",
        current_km: editing.current_km || 0,
        last_oil_change_km: editing.last_oil_change_km || 0,
        last_oil_change_date: editing.last_oil_change_date ? editing.last_oil_change_date.slice(0, 10) : todayIso(),
        oil_change_interval_km: editing.oil_change_interval_km || 5000,
        gps_device_id: editing.gps_device_id || "",
        notes: editing.notes || "",
      });
    } else {
      setForm(buildEmpty());
    }
  }, [editing, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        current_km: Number(form.current_km),
        last_oil_change_km: Number(form.last_oil_change_km),
        last_oil_change_date: form.last_oil_change_date
          ? new Date(form.last_oil_change_date + "T12:00:00").toISOString()
          : null,
        oil_change_interval_km: Number(form.oil_change_interval_km),
        gps_device_id: form.gps_device_id?.trim() || null,
        daily_rate: form.daily_rate === "" || form.daily_rate == null ? null : Number(form.daily_rate),
        image_url: form.image_url?.trim() || null,
      };
      if (editing) {
        await api.patch(`/cars/${editing.car_id}`, payload);
        toast.success(t("updatedCar"));
      } else {
        await api.post("/cars", payload);
        toast.success(t("createdCar"));
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl">{editing ? t("editCar") : t("addCar")}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
            {t("plateNumber")} · {t("make")} · {t("model")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("plateNumber")} required>
              <Input data-testid="car-form-plate" value={form.plate_number} onChange={(e) => setField("plate_number", e.target.value)} required className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("make")} required>
              <Input data-testid="car-form-make" value={form.make} onChange={(e) => setField("make", e.target.value)} required className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("model")} required>
              <Input data-testid="car-form-model" value={form.model} onChange={(e) => setField("model", e.target.value)} required className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("color")}>
              <Input data-testid="car-form-color" value={form.color} onChange={(e) => setField("color", e.target.value)} className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("intervalKm")}>
              <Input data-testid="car-form-interval" type="number" value={form.oil_change_interval_km} onChange={(e) => setField("oil_change_interval_km", e.target.value)} placeholder={t("intervalHint")} className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("currentKm")}>
              <Input data-testid="car-form-current-km" type="number" value={form.current_km} onChange={(e) => setField("current_km", e.target.value)} className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("lastOilKm")}>
              <Input data-testid="car-form-last-oil-km" type="number" value={form.last_oil_change_km} onChange={(e) => setField("last_oil_change_km", e.target.value)} className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("lastOilDate")}>
              <Input data-testid="car-form-last-oil-date" type="date" value={form.last_oil_change_date || ""} onChange={(e) => setField("last_oil_change_date", e.target.value)} dir="ltr" className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label={t("gpsDeviceId")}>
              <Input data-testid="car-form-gps-device" value={form.gps_device_id} onChange={(e) => setField("gps_device_id", e.target.value)} dir="ltr" placeholder={t("gpsDeviceHint")} className="bg-[#0A0A0A] border-white/10 font-mono" />
            </Field>
            <Field label="حالة السيارة">
              <select data-testid="car-form-status" value={form.status || "available"} onChange={(e) => setField("status", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm">
                <option value="available">متاحة</option>
                <option value="reserved">محجوزة</option>
                <option value="maintenance">صيانة</option>
              </select>
            </Field>
            <Field label="السعر اليومي">
              <Input data-testid="car-form-rate" type="number" value={form.daily_rate ?? ""} onChange={(e) => setField("daily_rate", e.target.value)} placeholder="15" className="bg-[#0A0A0A] border-white/10" />
            </Field>
            <Field label="رابط صورة السيارة">
              <Input data-testid="car-form-image" dir="ltr" value={form.image_url || ""} onChange={(e) => setField("image_url", e.target.value)} placeholder="https://…" className="bg-[#0A0A0A] border-white/10" />
            </Field>
          </div>
          <Field label={t("notes")}>
            <Textarea data-testid="car-form-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="bg-[#0A0A0A] border-white/10" />
          </Field>
          <DialogFooter className="gap-2">
            <button
              type="button"
              data-testid="car-form-cancel"
              onClick={() => onOpenChange(false)}
              className="border border-white/10 bg-transparent hover:bg-[#1C1C1C] px-4 py-2 text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              data-testid="car-form-save"
              disabled={saving}
              className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 disabled:opacity-60"
            >
              {t("save")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
      {label}{required && <span className="text-[#FF3B30]"> *</span>}
    </Label>
    {children}
  </div>
);
